import { Controller } from "@hotwired/stimulus"

// LRPO-5363 (code review) — the query-string parameter the pause HIGH-WATER MARK
// travels on. See _writeUrlMark for why the URL and not a cookie.
const PAUSE_MARK_PARAM = "paused_at"

// LRPO-5363 (code review, round 4) — the RESOLUTION of every scan timestamp the
// client is given. lead_scans.scan_time is a datetime(6), but LeadScan#broadcast,
// Exhibitor::LiveDashboardController#serialize_scan and _scan_entry all serialize it
// with `iso8601` — no fractional seconds — so a scan at 10:20:30.742 reaches us as
// "10:20:30" and its TRUE instant lies somewhere in [t, t + 1s). The pause mark is
// compared against the un-truncated column server-side (`scan_time <= paused_at`), so
// a floor taken from the truncated value would still exclude the very row it exists to
// protect. Round the floor up past the whole truncation interval. See _seenFloorMs.
const SCAN_TS_RESOLUTION_MS = 1000

/**
 * Scan-feed DOM owner — prepends new scans in coalesced requestAnimationFrame
 * batches, caps visible items, handles pause/buffer. All
 * construction is createElement + textContent (XSS-hardened).
 */
export default class extends Controller {
  static targets = ["list", "pauseButton", "buffered", "emptyState", "announcer"]
  static values  = {
    maxItems:   { type: Number, default: 100 },
    tz:         { type: String, default: "UTC" },
    paused:     { type: Boolean, default: false },
    // LRPO-5363 — per-event_exhibitor suffix for the persisted pause key so a
    // refresh restores THIS dashboard's pause and doesn't bleed across events.
    storageKey: { type: String, default: "" },
    // LRPO-5363 (QA re-open #2) — the SERVER's clock at render time (epoch-ms).
    // The pause mark is a cutoff the SERVER compares against scan timestamps, but
    // it is stamped here in the browser, so the two clocks have to be reconciled
    // or a user whose machine runs a few minutes slow would hide rows that were
    // never hidden. 0 = not supplied (older markup) → no correction.
    serverTime: { type: Number, default: 0 },
    // LRPO-5363 (code review) — the EVENT-TZ start of the day the server just
    // rendered (epoch-ms, server clock). The feed is today-scoped, so a pause mark
    // from a previous day can withhold nothing; the server drops it (see
    // LiveDashboardReport#process) and this lets the CLIENT drop the pause with it,
    // instead of leaving a "paused" button and banner sitting over a live list.
    // 0 = not supplied → no rollover handling (pre-fix behavior).
    dayStart: { type: Number, default: 0 },
    // LRPO-5363 (code review) — the mark the SERVER ACTUALLY APPLIED to the rows it
    // rendered into this page (epoch-ms; 0 = it rendered the LIVE feed). THIS is the
    // server-truth channel, and it replaces reading ?paused_at back off the URL: the
    // URL is whatever the CLIENT last wrote with history.replaceState, so reading it
    // back is the client believing its own claim. The server discards marks the
    // client keeps, and every time it did, a live list got rendered underneath a
    // client that still thought it was frozen. See _scan_feed.html.erb.
    renderedMark: { type: Number, default: 0 }
  }

  connect() {
    // LRPO-5135 — two SEPARATE queues. They were one shared `_pendingQueue`,
    // which leaked: a scan staged for animation pre-pause and a scan buffered
    // post-pause shared the same array, so the deferred animation timer could
    // shift a *buffered* scan and insert it into the live list while paused.
    //   _buffer    — scans held back WHILE PAUSED (drained on resume).
    //   _animQueue — scans staged for the next rAF batch flush (LRPO-5302).
    this._buffer = []
    this._animQueue = []
    // LRPO-5302 — the single armed rAF that drains _animQueue as ONE batch, and
    // its handle so disconnect() can cancel it. See _enqueueForAnimation.
    this._animFrame = null
    this._animFlushArmed = false
    this._bufferCount = 0
    // LRPO-5363 (rework) — the scan payloads for every row CURRENTLY on screen,
    // keyed by lead id, kept in step with the live list (_renderScans /
    // _insertOrReplace / _trim). It is the source the FROZEN snapshot is captured
    // from at pause, so the client can re-render the frozen feed on a refresh
    // instead of inheriting the server's current-content HTML. Empty for pure ERB
    // seed rows (the client has no payload for those until a poll reconciles them).
    this._displayed = new Map()
    // LRPO-5363 (rework) — the FROZEN display: the payloads the user was looking at
    // when the feed froze. Persisted alongside the buffer and re-rendered on a
    // refresh-while-paused so an EDIT/DELETE to an already-visible lead (whose
    // scan_time the pause mark cannot withhold) does not leak. Null while running.
    this._frozenScans = null
    // LRPO-5367 — pending lead-DATA edits (qualifier/attendee changes) that
    // arrived while paused. Unlike a new scan there's no row to buffer, so we
    // just flag it and surface "updates" in the banner; _stashedSnapshot holds
    // the freshest server list so a click can reconcile the changed rows on
    // demand while STAYING paused.
    this._pendingEdits = false
    this._stashedSnapshot = null
    // LRPO-5367 (QA re-open) — per-lead signature of the LAST paused poll
    // snapshot, so the NEXT one can tell an edited row from an unchanged one
    // without a cable broadcast. Null until the first paused poll baselines it
    // (see _reconcilePausedSnapshot).
    this._snapshotSignatures = null
    // LRPO-5367 (code-review re-open #3) — last known SERVER signature of the leads
    // the user can still SEE (a frozen row / a held buffer entry) but that the last
    // snapshot NO LONGER CARRIED. Those leads have left the snapshot window (aged
    // out behind 100 fresher scans, or the event-tz midnight `today_range`
    // rollover), so `_snapshotSignatures` drops them — but the user is still looking
    // at them, so the client must remember what it showed. A lead can re-enter the
    // window only by being RESCANNED, and this map is what lets the poll tell that
    // rescan (payload MOVED since we showed it) apart from the snapshot merely
    // CATCHING UP with a row the cable already put on screen (payload IDENTICAL).
    // Snapshot-derived only — never a cable payload (see _scanSignature).
    this._pinnedSignatures = new Map()
    // LRPO-5367 (code-review re-open) — every lead the user has been ACCOUNTED for:
    // rendered into the live list, or announced by the pending-changes banner. This —
    // not "does the snapshot lead have a row?" — is the baseline the paused diff
    // counts against; see _reconcilePausedSnapshot for why the DOM cannot be that
    // baseline, and _pruneSeenLeadIds for why the ROWS are nevertheless pinned INTO
    // it. Maintained at the four points a lead becomes known or forgotten:
    //   _renderScans       (a full server snapshot is on screen — rebuilds the set)
    //   _insertOrReplace   (a cable scan was inserted into the live list)
    //   _enqueueBuffered   (a scan was buffered and counted into the banner)
    //   _pruneSeenLeadIds  (bounds it to snapshot ∪ buffer ∪ rendered rows)
    this._seenLeadIds = new Set()
    // Whether _seenLeadIds has been anchored to a FULL SERVER SNAPSHOT. Until it
    // has, the client has no idea which leads the server considers "recent", so
    // it cannot classify anything as new. False on a cold page load; set by the
    // first snapshot we see (replaceFeed while running, or the baselining first
    // pass of _reconcilePausedSnapshot); restored from sessionStorage on a
    // refresh-while-paused so that refresh does NOT cost us a baseline.
    this._baselined = false
    this._lastAnnouncement = ""

    // LRPO-5363 (QA re-open #2) — the pause HIGH-WATER MARK: the server-clock
    // instant the feed was frozen at, in epoch-ms. This is the ONE piece of pause
    // state the SERVER also needs (it travels on the page URL — see _writeUrlMark),
    // because the feed rows are server-rendered and no amount of client-side
    // buffering can withhold a row the server already put in the HTML. Null
    // whenever the feed is running.
    this._pausedAtMs = null
    // Offset from this browser's clock to the server's, measured once from the
    // timestamp the ERB stamped into the markup at render. See _serverNow for why
    // this is deliberately a LOWER bound on the server's clock and not an attempt at
    // an accurate one.
    this._clockSkewMs = this.serverTimeValue ? this.serverTimeValue - Date.now() : 0
    // LRPO-5363 (code review, round 4) — THE FLOOR. The newest SERVER-ISSUED scan
    // instant (epoch-ms) the client has actually SHOWN the user: seeded from the
    // server-rendered rows' data-scan-at, then advanced by every scan that reaches the
    // LIVE list (a cable prepend, a poll snapshot, a buffer flush). NOT advanced by a
    // scan that is merely BUFFERED — those are withheld by definition. The pause mark
    // is floored at this, which is what makes "a row you were looking at cannot vanish
    // on refresh" a guarantee rather than a hope about clock accuracy. 0 = nothing
    // shown yet.
    this._newestSeenScanMs = 0
    this._staleFeedSignal = null

    // Seeded BEFORE the pause branches below: the rows the SERVER rendered are the
    // first thing the user sees, and on a cold connect they are the ONLY evidence of
    // how recent this feed is.
    this._seedNewestSeenScanFromDom()

    // LRPO-5363 (code review) — the mark the SERVER ACTUALLY RENDERED THIS PAGE
    // WITH (null ⇒ it rendered the LIVE feed). Read from the value the SERVER
    // stamped into the markup, NOT from ?paused_at on the URL: the URL is whatever
    // this controller last wrote with history.replaceState, so trusting it is the
    // client believing its own claim, and the server DISCARDS marks the client keeps
    // (garbage, a previous event-tz day, a future mark). Every discard used to leave
    // a LIVE feed rendered under a client that still thought it was frozen — banner
    // and buffer over rows sitting in plain sight. Reading the server's echo makes
    // each of those cases collapse into "renderedMark === null" automatically.
    const renderedMark = this._readRenderedMark()

    // LRPO-5363 — the pause toggle is a Stimulus Boolean value that reset to
    // false on every reconnect, so a page refresh silently resumed the feed.
    // Restore the persisted pause state (sessionStorage — survives F5 but clears
    // when the tab closes, so a stale pause never carries into a new session)
    // and re-render the button. Incoming scans then buffer via prependScan and
    // the poll's replaceFeed no-ops, exactly as after a manual pause.
    if (this._readPersistedPause()) {
      // LRPO-5363 (QA re-open) — the buffered scans/edit-flag accumulated
      // pre-refresh lived only in the _buffer/_pendingEdits instance fields
      // reset above, so restoring the pause flag alone left the banner and
      // pending count reset to zero. Restore them from the same store (it also
      // carries the mark — see _restorePersistedBuffer).
      this._restorePersistedBuffer()
      this._restorePauseState(renderedMark)
      // LRPO-5363 (rework) — re-render the FROZEN display over the server's
      // current-content HTML so an EDIT/DELETE to an already-visible lead does not
      // leak on refresh (the scan-time mark hides new SCANS, but an edited lead's
      // scan_time is unchanged, so the server re-includes its row with current data).
      // Guarded on pausedValue because _restorePauseState may have dropped a
      // stale-day pause, and on the renderedMark===null recovery path it clears the
      // frozen snapshot (the server served the LIVE feed — there is nothing to
      // re-freeze). Only re-render when we actually captured a frozen snapshot; a
      // cold pause (no poll yet) leaves it null and the ERB rows are kept as-is.
      if (this.pausedValue && Array.isArray(this._frozenScans) && this._frozenScans.length) {
        this._renderFrozen(this._frozenScans)
      }
    } else {
      // Drop any orphaned buffer (e.g. a prior write that raced a failed
      // pause-flag write) now that we know this session isn't paused.
      this._clearPersistedBuffer()

      // This session is NOT paused, so no mark belongs on this URL — strip whatever
      // is there unconditionally. It may be a param the SERVER rejected outright
      // (garbage, a previous day), which renderedMark below cannot see precisely
      // because the server threw it away; leaving it would re-send it on every
      // subsequent refresh.
      this._clearUrlMark()

      if (renderedMark !== null) {
        // (code review) A FROZEN feed rendered for a client that believes it is
        // LIVE — and now we know it from the SERVER's own echo, not from the URL we
        // just cleared. The mark is tab-scoped precisely so this is hard to reach,
        // but a URL carrying one can still be copied into a new tab, where
        // sessionStorage is empty. Repair what is already on screen: with a healthy
        // cable there may be no poll for minutes (live_dashboard_controller only
        // starts the interval poller on a cable failure), so "a poll will fix it" is
        // not true on a quiet booth.
        this._requestLiveRefresh()
      }
    }
  }

  // LRPO-5363 — reconcile a pause RESTORED from sessionStorage against what the
  // server actually rendered into the page (see connect()).
  _restorePauseState(renderedMark) {
    // Event-tz day rollover (code review): a PERSISTED mark from a PREVIOUS day is
    // dead. The feed is today-scoped, so there is nothing left for it to withhold;
    // the server ignores such a mark and renders today's live feed
    // (LiveDashboardReport#process), and a "Paused / N new scans" UI left sitting
    // over that live list would be a lie. Reachable simply by leaving the tab open
    // overnight on a multi-day event.
    //
    // Only the SESSIONSTORAGE mark is checked. renderedMark needs no stale-day test
    // of its own any more: it is the server's echo of what it APPLIED, and the server
    // has already dropped a stale-day mark before echoing (it would come back as 0 ⇒
    // null). Re-deriving the server's rules here is exactly the desync this fix
    // removes.
    if (this._isStaleDay(this._pausedAtMs)) {
      this._dropPause()
      return
    }

    this.pausedValue = true
    this._renderPauseButton(true)

    if (renderedMark === null) {
      // The server served the LIVE feed — because no mark reached it (sessionStorage
      // survives a same-tab Turbo navigation away and back, but the URL param does
      // not, so the restored pause can outlive the mark), or because it REJECTED the
      // one that did. Either way, everything the buffer was withholding is now ON
      // SCREEN, and the banner would be counting scans the user can plainly see.
      // Reset it and re-freeze from the render instant instead.
      this._resetBufferState()
      // (code review, round 4) Floored at the newest row actually on screen. The
      // render instant is the ERB's stamp, which a Turbo Drive restoration visit
      // serves from the snapshot CACHE — minutes old, with no network request — so on
      // its own it can freeze the feed BEHIND rows the cable/poll have since painted.
      this._pausedAtMs = Math.max(this.serverTimeValue || this._serverNow(), this._seenFloorMs())
    } else {
      this._pausedAtMs = renderedMark
    }

    // Re-assert the mark (a no-op when the URL already carries it) and re-persist,
    // so the NEXT refresh freezes at the SAME instant instead of creeping forward
    // one reload at a time — each creep would reveal another slice of the very
    // scans the banner says are hidden.
    this._writeUrlMark()
    this._writePersistedBuffer()
  }

  // Everything the pause is holding back, dropped: the feed on screen is live, so
  // the buffer/banner/seen-set have nothing left to say. _baselined goes with them —
  // the DOM was rebuilt by the SERVER, so the client's old anchor is void and the
  // next paused poll must re-baseline rather than banner what is already visible.
  _resetBufferState() {
    this._buffer = []
    this._bufferCount = 0
    this._pendingEdits = false
    this._stashedSnapshot = null
    this._snapshotSignatures = null
    this._pinnedSignatures = new Map()
    this._seenLeadIds = new Set()
    this._baselined = false
    // LRPO-5363 (rework) — the pause's frozen display goes with the rest of its
    // state. The one caller that reaches here while STILL paused is the
    // renderedMark===null recovery path, where the server served the LIVE feed, so
    // there is nothing left to re-freeze and the connect() guard must see it gone.
    this._frozenScans = null
    this._renderBufferedCount(0)
    this._clearPersistedBuffer()
  }

  _dropPause() {
    this.pausedValue = false
    this._pausedAtMs = null
    this._resetBufferState()
    this._writePersistedPause(false)
    this._renderPauseButton(false)
    this._clearUrlMark()
  }

  // LRPO-5302 — tear down the armed burst flush. Stimulus disconnects this
  // controller on a Turbo navigation / page swap; an rAF left armed would fire
  // afterwards against a detached list target (the leak class LRPO-5300 fixed
  // elsewhere — don't reintroduce it here). Idempotent: Stimulus can disconnect
  // more than once, so only cancel a handle we still hold.
  disconnect() {
    this._cancelAnimFlush()
    this._animQueue = []
    // LRPO-5363 (code review) — the deferred stale-feed signal must die with the
    // controller too, or a Turbo teardown leaves a timer that dispatches on a
    // detached element.
    if (this._staleFeedSignal) {
      clearTimeout(this._staleFeedSignal)
      this._staleFeedSignal = null
    }
  }

  prependScan(scan) {
    if (this.pausedValue) {
      this._pushToBuffer(scan)
      return
    }
    this._enqueueForAnimation(scan)
  }

  replaceFeed(scans) {
    // LRPO-5135 — while PAUSED the live list is frozen. The poll's snapshot
    // includes scans that arrived during the pause; rebuilding the list from it
    // would repopulate the feed and reset the buffer banner — re-leaking the
    // very scans we held back. Skip the swap while paused; the first poll after
    // resume reconciles to server truth.
    if (this.pausedValue) return
    this._renderScans(scans)
  }

  // LRPO-5367 — the actual list rebuild, split out of replaceFeed so an explicit
  // user action (showBuffered, while paused) can render a snapshot on demand
  // without the paused-guard early return. Clears the buffer/animation/edit
  // state since a full render supersedes anything held back.
  //
  // Both callers pass a full SERVER SNAPSHOT (the poll's recent_scans, or the
  // stash of one), so this is also where _seenLeadIds is anchored: after this
  // runs, the list IS the snapshot, so the snapshot's ids are exactly the leads
  // the user has been shown. Rebuilt (not merged) so the set stays bounded by the
  // snapshot window — a lead that has aged out of it can only reappear in a later
  // snapshot by being RESCANNED, which is genuine new activity and SHOULD count.
  _renderScans(scans) {
    if (!this.hasListTarget) return
    while (this.listTarget.firstChild) this.listTarget.removeChild(this.listTarget.firstChild)
    this._displayed = new Map() // LRPO-5363 (rework) — the list IS this snapshot now; rebuild the payload map
    scans.forEach(scan => {
      this.listTarget.appendChild(this._buildScanNode(scan))
      this._noteSeenScan(scan) // LRPO-5363 (round 4) — a full snapshot is now ON SCREEN; raise the floor
      const dk = this._leadKey(scan)
      if (dk !== null) this._displayed.set(dk, scan)
    })
    this._bufferCount = 0
    this._buffer = []
    this._animQueue = []
    this._cancelAnimFlush() // LRPO-5302 — this render supersedes the queued burst; drop its armed frame too
    this._pendingEdits = false
    this._snapshotSignatures = this._signaturesFor(scans)
    this._seenLeadIds = new Set(this._snapshotSignatures.keys())
    // The list IS the snapshot now, so no visible lead sits outside the window and
    // there is nothing left to pin (LRPO-5367 re-open #3).
    this._pinnedSignatures = new Map()
    this._baselined = true
    this._renderBufferedCount(0)
    this._syncEmptyState()
    // LRPO-5363 — a full render supersedes any persisted buffer. But when the
    // render happened WHILE PAUSED (showBuffered peeking at the stashed snapshot)
    // the persisted record must be REWRITTEN rather than dropped: it now also
    // carries the seen-set/baseline (LRPO-5367), and losing that would force the
    // next paused poll after a refresh to re-baseline — which is precisely the
    // state in which it cannot tell a new scan from the day's backlog.
    if (this.pausedValue) {
      // LRPO-5363 (rework) — this render (showBuffered revealing the stashed
      // snapshot) is now what is on screen, so it becomes the new frozen display:
      // a subsequent refresh must re-render THIS, not the pre-reveal rows.
      this._frozenScans = this._captureFrozen()
      this._writePersistedBuffer()
    } else {
      this._clearPersistedBuffer()
    }
  }

  // LRPO-5135 — public accessor so the parent dashboard controller can skip the
  // feed-list swap on a poll while still letting KPIs / rep-activity update.
  get isPaused() {
    return this.pausedValue
  }

  togglePause() {
    this.pausedValue = !this.pausedValue
    this._renderPauseButton(this.pausedValue)
    this._writePersistedPause(this.pausedValue) // LRPO-5363 — survive a refresh
    // LRPO-5363 (QA re-open #2) — stamp/clear the high-water mark BEFORE the
    // branches below, so the _writePersistedBuffer() calls in both of them
    // persist the mark alongside the buffer they are already saving.
    if (this.pausedValue) {
      this._pausedAtMs = this._stampPauseMark()
      this._writeUrlMark()
    } else {
      this._pausedAtMs = null
      this._clearUrlMark()
    }
    if (this.pausedValue) {
      // LRPO-5367 (code-review re-open) — snapshot the SEEN set the moment the
      // feed freezes, even with nothing pending yet. It is the baseline every
      // later paused poll diffs against, and a refresh must not cost us it: the
      // buffer record was previously only written once something was pending, so
      // a "pause, then refresh" lost the baseline and the first poll back had to
      // re-derive one from a snapshot that already contained the pause window's
      // scans — silently absorbing them.
      //
      // (QA re-open #2) This runs even when NO poll has happened yet — the poll
      // interval is 30-60s, so "open the dashboard, hit Pause" reaches here with an
      // empty _seenLeadIds and _baselined === false. That record is still written,
      // but it now carries `baselined: false`, so the restore knows its seen-set is
      // a placeholder and not an anchor. Writing it unconditionally is what keeps
      // any buffered scans/edit-flag on a pre-baseline pause alive across a refresh;
      // the FLAG — not the presence of the record — is what licenses the diff.
      //
      // LRPO-5363 (rework) — snapshot what is ON SCREEN at pause time so a refresh
      // can re-render the frozen feed rather than inherit the server's current
      // content. Null when the client has no payloads for the rendered rows yet
      // (a cold pause before the first poll) — see _captureFrozen.
      this._frozenScans = this._captureFrozen()
      this._writePersistedBuffer()
      return
    }
    // LRPO-5367 — on resume the live poll reconciles the feed (replaceFeed
    // runs again), so drop the paused-edit state; buffered scans still flush.
    // LRPO-5363 (rework) — reveal current server truth immediately instead of
    // leaving the frozen (possibly pre-edit) rows on screen until the next poll,
    // which the resume does not itself trigger. Only when nothing was buffered: a
    // non-empty buffer holds scans NEWER than the stashed snapshot, and _flushBuffer
    // prepends those; the debounced poll then reconciles any edits.
    const stashed = this._stashedSnapshot
    const hadBuffer = this._buffer.length > 0
    this._pendingEdits = false
    this._stashedSnapshot = null
    this._snapshotSignatures = null // next pause re-baselines against a fresh snapshot
    this._pinnedSignatures = new Map()
    this._frozenScans = null // resumed → nothing is frozen any more
    this._flushBuffer()
    if (!hadBuffer && Array.isArray(stashed) && stashed.length) this._renderScans(stashed)
  }

  // LRPO-5368 — reveal the scans buffered DURING the pause WITHOUT resuming live
  // updates. The "click to show" banner lets the user PEEK at what accumulated;
  // the feed must STAY paused until Resume is explicitly clicked. Previously this
  // banner was wired to togglePause, which un-paused as a side effect.
  // LRPO-5367 — if lead-DATA edits also arrived while paused, render the stashed
  // snapshot directly, which reconciles every changed row in one pass. Otherwise
  // just flush the buffered scans (5368). Either path stays paused — _renderScans /
  // _flushBuffer never touch pausedValue.
  //
  // The stash is NOT "full server truth": it is the LAST POLL's snapshot, so it is
  // up to one poll interval (30-60s) old, and any cable scan that landed after it
  // is in _buffer but NOT in it. (Which means this branch can drop a buffered scan
  // the banner already promised — PRE-EXISTING on staging, not introduced here;
  // tracked for a follow-up rather than widened into this fix.)
  //
  // LRPO-5363 (QA re-open #2) — this also ADVANCES the pause high-water mark. The
  // user has explicitly asked to see everything held back, and the banner drops to
  // zero pending, so nothing before this click is "hidden" any more: a refresh must
  // render the feed up to HERE, not re-hide what was just revealed. The feed stays
  // PAUSED, so anything arriving after this click is withheld again against the new
  // mark.
  //
  // (code review, round 4) The stamp happens AFTER the reveal, not before. The reveal
  // is precisely what turns those scans from WITHHELD into SEEN, so it is what raises
  // the floor the new mark is taken against (_stampPauseMark). Stamped BEFORE, the
  // mark came from the clock estimate alone — which runs early by the page-load
  // latency — and could land BEHIND the very rows this click had just put on screen,
  // so the next refresh hid them all over again. The re-persist below covers what the
  // _writePersistedBuffer() inside _renderScans/_flushBuffer no longer can, now that
  // the mark is settled after them rather than before.
  showBuffered() {
    const wasPaused = this.pausedValue

    if (this._pendingEdits && Array.isArray(this._stashedSnapshot)) {
      this._renderScans(this._stashedSnapshot)
      this._stashedSnapshot = null
    } else {
      this._flushBuffer()
    }

    if (!wasPaused) return
    this._pausedAtMs = this._stampPauseMark()
    this._writeUrlMark()
    this._writePersistedBuffer()
  }

  // LRPO-5367 — a lead-DATA edit (qualifier/attendee change) broadcast a refresh
  // while paused. It's not a new scan (nothing to buffer), so flag it and
  // re-render the banner so "updates" surfaces even with zero buffered scans.
  markPausedEdit() {
    if (!this.pausedValue) return
    this._pendingEdits = true
    this._renderBufferedCount(this._bufferCount)
    this._writePersistedBuffer() // LRPO-5363 — persist the edits flag alongside the buffer
  }

  // LRPO-5367 — the parent hands us the freshest snapshot on each paused poll so
  // showBuffered can reconcile the edited rows on demand. The FEED stays frozen
  // (no list render — that's the whole point of pause), but the INDICATOR is
  // derived from the snapshot too.
  //
  // (QA re-open) This used to be store-only, which made the paused
  // pending-changes indicator 100% cable-driven: the only things that could raise
  // it were prependScan (a buffered scan) and markPausedEdit (a refresh
  // broadcast). So whenever a broadcast never arrived — socket down, subscription
  // rejected, dropped frame — a paused feed showed NOTHING, even though this very
  // method was being handed full server truth every 30-60s. Reconciling here
  // makes the indicator TRANSPORT-INDEPENDENT: the cable stays the sub-second
  // fast path, and the poll guarantees the indicator appears within one poll
  // cycle no matter why a broadcast went missing.
  receivePausedSnapshot(scans) {
    if (!this.pausedValue || !Array.isArray(scans)) return
    this._stashedSnapshot = scans
    this._reconcilePausedSnapshot(scans)
  }

  // --- internal -----------------------------------------------------------

  // LRPO-5367 (QA re-open) — diff the poll snapshot against what the paused feed
  // has ACCOUNTED FOR, and raise the indicator for anything it hasn't.
  //
  // *** WHY THE DOM IS NOT THE BASELINE (LRPO-5367, code-review re-open) ***
  // This method used to treat "a snapshot lead with no live-scan-<id> row in the
  // list" as "a scan the user has not seen". That premise requires the rendered
  // list to span the same window as the snapshot. IT DOES NOT — and it never did:
  //   • the ERB seeds the feed with the last 10 scans
  //     (LiveDashboardReport#build_recent_scans, .limit(10))
  //   • GET /exhibitor/live/snapshot serializes the last 100
  //     (Exhibitor::LiveDashboardController#snapshot, .limit(100))
  // and while paused replaceFeed is skipped BY DESIGN, so the DOM never grows to
  // the snapshot window. A booth with 100 scans today that pauses and refreshes
  // therefore rendered 10 rows, polled 100 leads, and banner-ed the other 90 as
  // "new scans" within a second of load — with zero new activity. Worse, clicking
  // it flushed those 90 OLDER scans through the prepending _insertOrReplace path,
  // landing them ABOVE the 10 genuinely-newest rows: an inverted feed.
  //
  // So the baseline is _seenLeadIds — an explicit record of the leads the client
  // has shown or announced — anchored to a full SERVER snapshot (see connect()).
  // The DOM is out of the correctness argument for new scans entirely, and no
  // server change is needed: the two limits may safely differ.
  //
  // Two kinds of pending change:
  //   NEW SCANS — a snapshot lead not in _seenLeadIds. Self-dedups against the
  //     cable (a broadcast scan was added to the set when it was buffered), so the
  //     two transports never double-count one lead, and against the CAPPED _buffer
  //     array (a scan shifted out of it stays in the set, so a later poll doesn't
  //     re-count it). Until the set has been anchored (_baselined — a cold load
  //     that went straight to paused), the FIRST snapshot is taken AS the baseline
  //     and counts nothing: with no server truth to compare against, "everything I
  //     can't see is new" is a guess, and the honest answer is to start counting
  //     from here. This mirrors what the edit branch below already does via its
  //     `previous &&` guard.
  //   CHANGES — a lead ALREADY seen whose payload moved (qualifiers/name/timestamp/
  //     scan_count), compared snapshot-to-snapshot.
  //
  // *** AND WHY THE DOM IS NOT THE BASELINE FOR *CHANGES* EITHER (re-open #2) ***
  // The change branch used to be gated on "is this stale row actually on screen?"
  // (rendered.has(key)). That is a DISPLAY-STALENESS question — sound for an EDIT,
  // but a RESCAN is NEW ACTIVITY, not a stale row, and the gate silently dropped it:
  // after the refresh-while-paused flow the DOM holds 10 ERB rows while the seen-set
  // holds all 100 snapshot ids (the very window gap above), so a rescan of a lead in
  // that 90-wide gap was SEEN (⇒ not new) and UNRENDERED (⇒ not an edit) and fell
  // between both branches. dirty stayed false, the new signature was absorbed, and
  // real scan activity was INVISIBLE until the user resumed — with the cable down,
  // which is this fix's own motivating scenario. The event-tz midnight `today_range`
  // rollover reached the same silence by a different road (see _pruneSeenLeadIds).
  //
  // So the DOM is no longer a GATE on the diff: the change branch fires for any seen
  // lead whose server signature moved, EXCEPT one sitting in _buffer. That widening
  // is phantom-free by construction:
  //   • both sides of `previous.get(key) !== signature` come from the SAME server
  //     serializer (_snapshotSignatures is only ever built from a snapshot — never
  //     from the DOM, whose ERB markup isn't the JSON, and never from a cable
  //     payload), so it can only fire on a genuine server-side payload change;
  //   • `previous` is null until a snapshot has been recorded, so the first poll
  //     after a refresh cannot fire it at all.
  // BUFFERED leads are excluded because an edit to an already-counted pending scan
  // is not a second pending item — the user cannot see its stale payload, and the
  // second pass below refreshes the buffered copy in place so revealing it later
  // shows current data rather than the pre-edit broadcast. That exclusion is also
  // what stops the cable and the poll BOTH reporting one rescan (the cable buffers
  // it and counts it; the poll must not then tack "+ updates" on top).
  //
  // *** BUT THE ROWS ARE STILL WHAT THE USER CAN SEE (re-open #3) ***
  // Taking the DOM out of the GATE is not the same as taking it out of the SEEN-SET,
  // and conflating the two opened a fifth phantom: a lead that showBuffered had
  // FLUSHED INTO THE LIST was in neither `signatures` nor `_buffer`, so the prune
  // evicted it the moment a snapshot predating that scan landed — and the next poll
  // re-reported a row the user is looking at as a brand-new scan. The seen-set pins
  // the rendered rows, and _pruneSeenLeadIds carries each pinned lead's last known
  // server signature so the fourth branch below can still tell a genuine RESCAN
  // (payload moved since we showed it) from the poll merely CATCHING UP with a row
  // the cable already put on screen (payload identical / never seen in a snapshot).
  //
  // The residual cost is unchanged: a change landing before the first paused
  // snapshot is caught only by the cable's markPausedEdit — the poll has to baseline
  // on its first pass. A missed-but-broadcast edit beats a false banner.
  _reconcilePausedSnapshot(scans) {
    const previous   = this._snapshotSignatures
    const pinned     = this._pinnedSignatures
    const signatures = new Map()
    const bySnapshot = new Map()
    const baselining = !this._baselined
    let   dirty      = false

    // Snapshot the buffer's keys BEFORE the loop — _enqueueBuffered mutates
    // _buffer (and its cap can shift entries out) as new scans are discovered.
    const buffered = new Set()
    this._buffer.forEach(entry => {
      const key = this._leadKey(entry)
      if (key !== null) buffered.add(key)
    })

    // Iterate OLDEST → NEWEST (the server serializes recent_scans newest-first):
    // _flushBuffer prepends each entry, so buffering in ascending-age order is what
    // leaves the newest scan on top once the user reveals them.
    for (let i = scans.length - 1; i >= 0; i--) {
      const scan = scans[i]
      const key  = this._leadKey(scan)
      if (key === null) continue // legacy payloads without a lead_id can't be diffed

      const signature = this._scanSignature(scan)
      signatures.set(key, signature)
      bySnapshot.set(key, scan)

      if (baselining) {
        this._seenLeadIds.add(key) // anchor only — deliberately NOT counted
        continue
      }

      if (!this._seenLeadIds.has(key)) {
        this._enqueueBuffered(scan) // counts it + marks it seen; banner/persist batched below
        dirty = true
      } else if (buffered.has(key)) {
        // Already counted as a pending new scan and the user cannot see its payload,
        // so a move on it is not a SECOND pending item. The second pass below
        // refreshes the held copy in place instead. This is also what stops the cable
        // and the poll BOTH reporting one rescan as "1 new scan + updates".
      } else if (previous && previous.has(key)) {
        if (previous.get(key) !== signature) {
          // A lead that was in the LAST snapshot and is still in this one has MOVED on
          // the server — a rescan (timestamp/scan_count) or an edit (qualifiers/name).
          // Either way what the user is looking at is out of date. Flag it; the banner
          // folds "+ updates" in alongside any buffered-scan count. Deliberately NOT
          // asked of the DOM: a rescan of a lead outside the 10 seeded rows is new
          // activity, not a stale row (see the note above).
          this._pendingEdits = true
          dirty = true
        }
      } else if (pinned && pinned.has(key) && pinned.get(key) !== signature) {
        // The lead is SEEN and was NOT in the last snapshot, but the user can still
        // see it (a frozen row / a held buffer entry) — so it had LEFT the snapshot
        // window and has now RE-ENTERED it. The only way back in is last_scan_at
        // moving up, i.e. a RESCAN: genuine new activity, so count it as a new scan
        // rather than a stale-row "update". This is the event-tz midnight
        // `today_range` rollover (the snapshot empties overnight; the next morning's
        // rescan of a still-frozen row must be announced) and the age-out case.
        this._enqueueBuffered(scan)
        dirty = true
      }
      // Remaining fall-through, and the reason the seen-set pins the ROWS: a seen
      // lead with NO recorded signature at all — the poll simply had not caught up
      // with a row the CABLE already put on screen (see _pruneSeenLeadIds). The
      // snapshot arriving with it is not news; saying so would banner a row the user
      // is looking at.
    }

    // A lead that is BUFFERED (not rendered) can be edited too. No banner change —
    // it is already counted as a pending new scan and the user cannot see its stale
    // payload — but the copy we would flush must carry the edit, or showBuffered
    // reveals the pre-edit broadcast. Done in a second pass so the capped
    // _buffer.shift() above cannot invalidate an index mid-loop.
    this._buffer.forEach((entry, idx) => {
      const key = this._leadKey(entry)
      if (key === null) return
      const fresh = bySnapshot.get(key)
      if (!fresh || this._scanSignature(entry) === signatures.get(key)) return
      this._buffer[idx] = fresh
      dirty = true
    })

    if (baselining) this._baselined = true

    // ONE banner render + ONE sessionStorage write for the whole snapshot. The
    // per-scan _pushToBuffer path wrote (and JSON-serialized the entire buffer)
    // once per discovered lead, so a snapshot carrying M new scans cost M
    // serializations and M synchronous storage writes on the main thread.
    if (dirty) this._renderBufferedCount(this._bufferCount)

    // Persist on a change OR on the pass that just ANCHORED the baseline. A
    // baselining pass is `dirty === false` by construction (it counts nothing), so
    // a dirty-only write left the anchor it had just established in memory only —
    // and a SECOND refresh would drop back to unbaselined and silently absorb the
    // scans of the next poll window into the "backlog". The write is what makes the
    // baseline survive a refresh, so it has to happen exactly when one is set.
    if (dirty || baselining) this._writePersistedBuffer()

    // Rebuild (not merge) so the map tracks only the current snapshot window and
    // can't grow unbounded across a long pause. Prune FIRST — it needs the outgoing
    // maps to carry a departed-but-still-visible lead's last known signature over.
    this._pruneSeenLeadIds(signatures, previous, pinned)
    this._snapshotSignatures = signatures
  }

  // Bound _seenLeadIds to what the user can still be shown: the CURRENT SNAPSHOT
  // WINDOW, the RETAINED BUFFER, and the ROWS FROZEN ON SCREEN. All three are seen
  // by definition, so this only DROPS ids — leads that have aged out of the feed AND
  // scrolled off the frozen list. The bound stays ~O(300): snapshot <= 100, rendered
  // <= maxItems (100), buffer <= maxItems (100).
  //
  // *** WHY THE ROWS MUST BE PINNED (re-open #3 — the phantom the widening opened) ***
  // Keeping only `signatures ∪ buffer` evicted anything showBuffered had FLUSHED into
  // the DOM: once revealed, a lead is in the buffer no longer, and the poll is async
  // (`setInterval(() => this._poll())` plus a debounced `_pollImmediately`, neither
  // awaited), so a response the server built BEFORE that scan existed can land AFTER
  // the user revealed it — carrying neither the lead nor, therefore, its id into
  // `keep`. The NEXT poll then found it unseen and re-reported a row the user is
  // LOOKING AT as a brand-new scan: a phantom banner plus a false aria-live
  // announcement. The seen-set has to retain what the user can actually SEE, not just
  // whatever the last snapshot happened to contain.
  //
  // *** AND WHY PINNING ALONE IS NOT ENOUGH (it re-breaks the rollover) ***
  // Pinning a row keeps it SEEN forever, which is precisely how the midnight rollover
  // used to swallow a rescan: at the event-tz `today_range` rollover the snapshot
  // empties, so every lead drops out of `signatures` AND out of `previous` — and a
  // still-frozen row that stayed seen was then neither "new" (seen) nor "changed" (no
  // `previous` entry to differ from) when it was rescanned the next morning. So the
  // pin carries the lead's LAST KNOWN SERVER SIGNATURE with it (_pinnedSignatures),
  // and _reconcilePausedSnapshot compares against that when the lead RE-ENTERS the
  // window. Membership cannot tell the two cases apart — the signature can:
  //   • re-enters with a MOVED payload  → it was RESCANNED     → count it (rollover)
  //   • re-enters with the SAME payload → the poll caught up   → silence  (phantom)
  //   • never had a signature at all    → cable-fed row, poll  → silence  (phantom)
  //     had simply not reached it yet
  // Phantom-free by construction: the pinned side is snapshot-derived like the fresh
  // side (never the DOM, never a cable payload — see _scanSignature), so a mismatch
  // can only mean a genuine server-side change.
  _pruneSeenLeadIds(signatures, previous, pinned) {
    const keep    = new Set(signatures.keys())
    const carried = new Map()

    // A lead the user can still see but the CURRENT snapshot no longer carries: keep
    // it seen, and carry its last known server signature (from the outgoing snapshot,
    // else from the outgoing pin — a lead can sit outside the window for many polls).
    const pin = (key) => {
      if (key === null || signatures.has(key)) return
      keep.add(key)
      if (carried.has(key)) return
      const last = (previous && previous.get(key)) ?? (pinned && pinned.get(key))
      if (last !== undefined) carried.set(key, last)
    }

    this._buffer.forEach(scan => pin(this._leadKey(scan)))
    this._renderedLeadIds().forEach(pin)

    this._seenLeadIds = keep
    this._pinnedSignatures = carried
  }

  // The rendered rows are the frozen list's own <li id="live-scan-<lead_id>"> nodes —
  // the same stable key the ERB initial render, the cable broadcast and the poll
  // payload all already agree on (LRPO-5242).
  _renderedLeadIds() {
    const ids = new Set()
    if (!this.hasListTarget) return ids
    this.listTarget.querySelectorAll("[data-live-scan-item]").forEach(node => {
      if (node.id.startsWith("live-scan-")) ids.add(node.id.slice("live-scan-".length))
    })
    return ids
  }

  // LRPO-5363 (rework) — snapshot the payloads for the rows CURRENTLY on screen, in
  // DOM order (newest-first), so a refresh-while-paused can re-render the frozen feed
  // instead of inheriting the server's current-content HTML. The scan-time pause mark
  // hides new SCANS on refresh but an EDIT/DELETE to an already-visible lead leaves
  // its scan_time untouched, so the server re-includes that row with CURRENT content;
  // the only place the pre-pause content still exists is here on the client.
  //
  // Returns null unless EVERY rendered row has a known payload. A bare ERB-seed row
  // the poll has not reconciled yet has none — and re-rendering a partial snapshot
  // would DROP those rows, so in that case we return null and leave the server HTML
  // alone (no worse than before this fix; the mark still covers the new-scan case).
  _captureFrozen() {
    if (!this.hasListTarget) return null
    const rows = this.listTarget.querySelectorAll("[data-live-scan-item]")
    if (!rows.length) return null
    const frozen = []
    for (const node of rows) {
      if (!node.id.startsWith("live-scan-")) return null
      const payload = this._displayed.get(node.id.slice("live-scan-".length))
      if (!payload) return null
      frozen.push(payload)
    }
    return frozen
  }

  // LRPO-5363 (rework) — re-render the FROZEN rows over whatever the server rendered.
  // DOM + _displayed only: it deliberately does NOT touch the buffer / seen-set /
  // baseline / banner (all restored from the persisted record) and does not persist —
  // the snapshot it draws from is already what was persisted. Same XSS-hardened
  // _buildScanNode path as every other render.
  _renderFrozen(scans) {
    if (!this.hasListTarget || !Array.isArray(scans) || !scans.length) return
    while (this.listTarget.firstChild) this.listTarget.removeChild(this.listTarget.firstChild)
    this._displayed = new Map()
    scans.forEach(scan => {
      this.listTarget.appendChild(this._buildScanNode(scan))
      this._noteSeenScan(scan)
      const key = this._leadKey(scan)
      if (key !== null) this._displayed.set(key, scan)
    })
    this._syncEmptyState()
  }

  _leadKey(scan) {
    return scan && scan.lead_id != null ? String(scan.lead_id) : null
  }

  // key → payload signature for a whole snapshot. Seeding this from the rows
  // _renderScans just drew means the FIRST paused poll can already tell an edited
  // row from an unchanged one, instead of spending its first pass baselining.
  _signaturesFor(scans) {
    const map = new Map()
    scans.forEach(scan => {
      const key = this._leadKey(scan)
      if (key !== null) map.set(key, this._scanSignature(scan))
    })
    return map
  }

  // Every field the feed row actually renders (see _buildScanNode) — a change in
  // any of them means the visible row is out of date. Built from the poll payload
  // only, never from the DOM, so both sides of the comparison come from the same
  // server serializer.
  _scanSignature(scan) {
    return JSON.stringify([
      scan.attendee_name || "",
      scan.company || "",
      scan.rep_name || "",
      scan.timestamp || "",
      scan.scan_count == null ? "" : String(scan.scan_count),
      Array.isArray(scan.qualifiers) ? scan.qualifiers : []
    ])
  }

  // LRPO-5363 — persisted pause state. sessionStorage is wrapped in try/catch
  // because it throws in private-mode / storage-disabled browsers; a failure
  // just falls back to the pre-fix behavior (pause not remembered) rather than
  // breaking the feed.
  _pauseStorageKey() {
    return `leadsHub.liveFeed.paused.${this.storageKeyValue || "default"}`
  }

  _readPersistedPause() {
    try {
      return window.sessionStorage.getItem(this._pauseStorageKey()) === "1"
    } catch (_e) {
      return false
    }
  }

  _writePersistedPause(paused) {
    try {
      if (paused) {
        window.sessionStorage.setItem(this._pauseStorageKey(), "1")
      } else {
        window.sessionStorage.removeItem(this._pauseStorageKey())
      }
    } catch (_e) {
      /* storage unavailable — pause simply won't persist across refresh */
    }
  }

  // LRPO-5363 (QA re-open) — persisted PENDING BUFFER (buffered scans + true
  // total count + pending-edit flag). Companion to the pause flag above: that
  // fix alone restored the paused BUTTON on refresh, but _buffer/_bufferCount/
  // _pendingEdits were reinitialized fresh in connect() every time, so the "N
  // new scans — click to show" banner silently reset to nothing. Same
  // sessionStorage key family (scoped per event_exhibitor) and try/catch
  // rationale as the pause flag.
  _bufferStorageKey() {
    return `leadsHub.liveFeed.buffer.${this.storageKeyValue || "default"}`
  }

  // Buffer state mutation ONLY (no banner render, no storage write) so a caller
  // holding many scans — _reconcilePausedSnapshot draining a whole poll snapshot —
  // can do both once for the batch instead of once per scan. Caps the RETAINED
  // array at maxItemsValue so a long pause can't grow the sessionStorage payload
  // unbounded; _bufferCount still tracks the TRUE total for the banner even once
  // the oldest replay entries are dropped, and _seenLeadIds keeps a shifted-out
  // lead from being counted twice by a later poll.
  _enqueueBuffered(scan) {
    this._buffer.push(scan)
    if (this._buffer.length > this.maxItemsValue) this._buffer.shift()
    this._bufferCount++
    const key = this._leadKey(scan)
    if (key !== null) this._seenLeadIds.add(key) // announced ⇒ accounted for
  }

  // Single push path for ONE buffered scan — prependScan (a cable broadcast that
  // lands while paused) is the ONLY caller, so that entry is counted, rendered and
  // persisted in one go. The BATCH callers (the mid-flight pause re-check in
  // _drainAnimQueue, and _reconcilePausedSnapshot draining a poll snapshot) go
  // straight to _enqueueBuffered and render + persist ONCE for the whole batch —
  // routing them through here re-serialized the entire buffer to sessionStorage
  // once per scan.
  _pushToBuffer(scan) {
    this._enqueueBuffered(scan)
    this._renderBufferedCount(this._bufferCount)
    this._writePersistedBuffer()
  }

  _writePersistedBuffer() {
    try {
      window.sessionStorage.setItem(this._bufferStorageKey(), JSON.stringify({
        scans: this._buffer,
        count: this._bufferCount,
        pendingEdits: this._pendingEdits,
        // LRPO-5367 (code-review re-open) — the pending COUNT is meaningless
        // without the baseline it was counted against. Carry the seen-set across
        // the refresh so the first paused poll on the restored page can still tell
        // a scan that arrived DURING the pause from the day's existing backlog
        // (the DOM can't: the ERB re-seeds only the last 10 rows, the snapshot
        // carries 100). Ids only — a few hundred bytes.
        seen: Array.from(this._seenLeadIds),
        // *** AND WHETHER THAT SET IS AN ANCHOR OR JUST AN EMPTY PLACEHOLDER. ***
        // (LRPO-5367, QA re-open #2.) An EMPTY seen-set is ambiguous: it is the
        // legitimate state of a booth whose poll returned zero scans, and it is
        // also the state of a controller that has NEVER SEEN A POLL — and those
        // two demand opposite readings of the next snapshot (count it all / count
        // none of it). The restore used to infer baselined-ness from the record's
        // mere PRESENCE, so a user who paused before the first poll (the poll runs
        // every 30-60s, so this is the ordinary result of "open dashboard, click
        // Pause") persisted `seen: []`, and the restored page — believing itself
        // anchored against nothing — banner-ed the ENTIRE DAY'S BACKLOG as new.
        // The flag is the disambiguator; it must ride along with the set.
        baselined: this._baselined,
        // LRPO-5363 (QA re-open #2) — the pause HIGH-WATER MARK, kept here as well
        // as on the URL. The URL is what the SERVER reads (and therefore what the
        // rendered page is FROZEN AT — so on connect it wins); this copy is the
        // client's own record, and is what tells a restored pause whether the page
        // it woke up in was rendered at its mark or not (see _restorePauseState).
        pausedAt: this._pausedAtMs,
        // LRPO-5363 (rework) — the FROZEN display: the scan payloads on screen when
        // the feed froze. On a refresh-while-paused the client re-renders these over
        // the server's current-content HTML, so an EDIT/DELETE to an already-visible
        // lead stays hidden until the user shows/resumes. Null on a cold pause (no
        // payloads captured yet) — the restore then leaves the ERB rows untouched.
        frozen: this._frozenScans
      }))
    } catch (_e) {
      /* storage unavailable/quota exceeded — buffer simply won't survive a refresh */
    }
  }

  _restorePersistedBuffer() {
    try {
      const raw = window.sessionStorage.getItem(this._bufferStorageKey())
      if (!raw) return
      const parsed = JSON.parse(raw)
      const scans = Array.isArray(parsed && parsed.scans) ? parsed.scans : []
      this._buffer = scans.slice(-this.maxItemsValue)
      this._bufferCount = Number.isFinite(parsed && parsed.count) ? parsed.count : this._buffer.length
      this._pendingEdits = !!(parsed && parsed.pendingEdits)
      // LRPO-5363 (QA re-open #2) — the mark the SERVER filtered this page's feed
      // by. Restoring it is what keeps the NEXT refresh frozen at the SAME instant
      // instead of creeping forward one reload at a time (each creep would reveal
      // another slice of the very scans the banner says are hidden).
      if (Number.isFinite(parsed && parsed.pausedAt)) this._pausedAtMs = parsed.pausedAt
      // LRPO-5363 (rework) — the frozen display captured at pause. Re-rendered by
      // connect() over the server's current-content HTML so a hidden edit/delete
      // does not leak. Absent (older record / cold pause) ⇒ null ⇒ leave the rows.
      this._frozenScans = Array.isArray(parsed && parsed.frozen) ? parsed.frozen : null
      // Trust the persisted seen-set ONLY when the record also says it was ANCHORED
      // to a real server snapshot. Two records fail this gate, and both must fall
      // back to re-baselining on the first paused poll rather than be read as
      // "anchored against nothing" (which banners the whole backlog — the bug):
      //   • baselined:false — the user paused BEFORE the first poll ever ran, so
      //     the set is an empty placeholder, not an anchor.
      //   • no `seen`/`baselined` key at all — a record written by an older deploy.
      // Re-baselining costs at most one poll cycle of silence for scans that landed
      // in the reload window; believing a phantom anchor costs the user a lie.
      // The buffered scans/edit-flag above are restored EITHER WAY — they were
      // counted from real cable broadcasts and are independent of the baseline.
      if (Array.isArray(parsed && parsed.seen) && parsed.baselined === true) {
        this._seenLeadIds = new Set(parsed.seen.map(String))
        this._baselined = true
      }
      this._renderBufferedCount(this._bufferCount)
    } catch (_e) {
      // Corrupted/stale entry (e.g. hand-edited storage, a schema change from
      // an older deploy) — start clean rather than throwing out of connect().
      this._buffer = []
      this._bufferCount = 0
      this._pendingEdits = false
      this._seenLeadIds = new Set()
      this._baselined = false
      this._frozenScans = null
    }
  }

  _clearPersistedBuffer() {
    try {
      window.sessionStorage.removeItem(this._bufferStorageKey())
    } catch (_e) {
      /* storage unavailable — nothing to clear */
    }
  }

  // LRPO-5363 (QA re-open #2) — THE PAUSE HIGH-WATER MARK, shipped to the server.
  //
  // Everything above this point is client state, and client state cannot fix this
  // bug: the Live Scan Feed's rows are SERVER-RENDERED, so on a refresh the scans
  // the buffer is withholding come back down inside the initial HTML no matter what
  // the client remembers. (That is exactly what QA kept re-opening: after #4475 and
  // #4599 the pause button and the "N new scans" banner both survived a refresh —
  // and the hidden scans were sitting in the table behind them.)
  //
  // So the mark travels on the PAGE URL (?paused_at=<epoch-ms>), kept in sync with
  // history.replaceState. Exhibitor::LiveDashboardController#paused_feed_cutoff
  // validates it and hands it to LiveDashboardReport, which renders the feed AS OF
  // that instant.
  //
  // WHY NOT A COOKIE (code review). The first cut used a session cookie, on the
  // theory that it "matches sessionStorage's lifetime". It does not: a cookie is
  // scoped to the BROWSER SESSION, sessionStorage to the TAB. So a second tab on
  // /exhibitor/live — empty sessionStorage, therefore rendering itself UNPAUSED —
  // would still SEND the first tab's mark and be served a feed frozen at someone
  // else's pause: rows missing, timestamps and "Scans: N" badges truncated, and no
  // banner to explain it. The URL has exactly sessionStorage's scope:
  //   * an F5 keeps it            → the mark survives the refresh (the ticket);
  //   * a new tab from the nav has no param → no phantom freeze;
  //   * two tabs cannot clobber each other → no per-event_exhibitor key needed.
  // (A mark copy-pasted into a fresh tab is still possible; connect() strips it and
  // forces an immediate live re-render — see _requestLiveRefresh.)

  // LRPO-5363 (code review, round 4) — THE MARK, and the two-sided guarantee it rests
  // on. Read this before touching _serverNow or the floor; they are a matched pair.
  //
  // A mark that is too EARLY silently DELETES rows: the server freezes the feed as of
  // it, so a scan the cable already prepended — that the user is looking at — is not
  // in the frozen feed on the next refresh, is not in the buffer (it predates the
  // pause), is not in the banner, and is already in _seenLeadIds so the paused poll
  // will not re-offer it. It just disappears, which is precisely what
  // build_paused_feed's docstring promises cannot happen. A mark that is too LATE
  // instead LEAKS: it sweeps a withheld scan into the server-rendered frozen feed
  // while the banner still counts it as hidden — the original QA bug.
  //
  // So the mark is SANDWICHED between two bounds that are each independently sound,
  // rather than pinned to one clock estimate that has to be accurate to be safe:
  //
  //   LOWER — _seenFloorMs(): the newest SERVER-ISSUED timestamp among the scans the
  //     client has actually shown. Server-issued, so it needs no clock correction at
  //     all, and by construction every visible row is at or before it. This is what
  //     makes "a row you are looking at cannot vanish" a guarantee.
  //
  //   UPPER — _serverNow(): Date.now() + (serverTime - Date.now()-at-connect). That
  //     estimate is contaminated by the page-load latency L (the server evaluates
  //     Time.current mid-render; connect() runs only after the render tail, the
  //     transfer, the parse and the pack eval), so it returns (true server now - L)
  //     — and on a Turbo Drive restoration visit, which re-renders the CACHED body
  //     with no network request, L is however long the user was away. That makes it a
  //     poor ESTIMATE but a sound UPPER BOUND: L >= 0 always, so it can never run
  //     ahead of the server's clock, and the mark can therefore never be stamped in
  //     the future. The floor supplies the accuracy the estimate lacks.
  //
  // Both bounds are <= the server's true now, so max() of them is too — with one
  // deliberate exception, bounded by a second: SCAN_TS_RESOLUTION_MS (see
  // _seenFloorMs). The server clamps a future mark to Time.current anyway.
  _stampPauseMark() {
    return Math.max(this._serverNow(), this._seenFloorMs())
  }

  // This browser's clock corrected onto the server's — the mark is compared against
  // server-side scan timestamps, so it has to be stamped in the server's frame. A
  // LOWER bound on the server's now, never an over-estimate. See _stampPauseMark.
  _serverNow() {
    return Date.now() + this._clockSkewMs
  }

  // The floor: the newest scan the user has been SHOWN, pushed past the truncation
  // interval its serialized timestamp arrived in (SCAN_TS_RESOLUTION_MS) so the
  // server's comparison against the un-truncated datetime(6) column cannot exclude
  // the very row this exists to protect. 0 when nothing has been shown — there is
  // nothing to protect, so the estimate stands alone.
  _seenFloorMs() {
    if (!this._newestSeenScanMs) return 0
    return this._newestSeenScanMs + SCAN_TS_RESOLUTION_MS
  }

  // A scan has been RENDERED INTO THE LIVE LIST — the user can see it. Deliberately
  // NOT called from _enqueueBuffered: a buffered scan is withheld, and letting it
  // raise the floor would sweep it into the frozen feed the server renders on refresh
  // while the banner still claimed it was hidden.
  _noteSeenScan(scan) {
    const ms = this._scanInstantMs(scan && scan.timestamp)
    if (ms !== null && ms > this._newestSeenScanMs) this._newestSeenScanMs = ms
  }

  // The rows the SERVER rendered into the initial HTML. On a cold connect they are the
  // only evidence the client has of how recent the feed it is showing actually is —
  // and the case this whole ticket is about (refresh while paused) IS a cold connect.
  // data-scan-at is each row's AS-OF instant, which while paused is its FROZEN
  // (pre-pause) instant, not leads.last_scan_at — see _scan_entry.html.erb.
  _seedNewestSeenScanFromDom() {
    if (!this.hasListTarget) return
    this.listTarget.querySelectorAll("[data-live-scan-item]").forEach(node => {
      const ms = this._scanInstantMs(node.getAttribute("data-scan-at"))
      if (ms !== null && ms > this._newestSeenScanMs) this._newestSeenScanMs = ms
    })
  }

  // iso8601 — the format the cable payload, the poll payload and data-scan-at share.
  _scanInstantMs(value) {
    if (!value) return null
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? ms : null
  }

  // The mark the SERVER rendered this page with, or null when it rendered the LIVE
  // feed. Straight off the server's echo (0/absent ⇒ live), so there is nothing to
  // re-validate here: the server already applied every rule it has — the digits-only
  // check, the future-mark clamp (Exhibitor::LiveDashboardController#paused_feed_cutoff)
  // and the stale-day drop (LiveDashboardReport#process) — and this is the result.
  // Re-deriving those rules on the client is precisely how the two got out of step.
  _readRenderedMark() {
    const mark = this.renderedMarkValue
    return Number.isFinite(mark) && mark > 0 ? mark : null
  }

  _writeUrlMark() {
    if (!this._pausedAtMs) return
    this._replaceUrlMark(String(this._pausedAtMs))
  }

  _clearUrlMark() {
    this._replaceUrlMark(null)
  }

  _replaceUrlMark(value) {
    try {
      const url = new URL(window.location.href)
      if (value === null) {
        if (!url.searchParams.has(PAUSE_MARK_PARAM)) return
        url.searchParams.delete(PAUSE_MARK_PARAM)
      } else {
        if (url.searchParams.get(PAUSE_MARK_PARAM) === value) return
        url.searchParams.set(PAUSE_MARK_PARAM, value)
      }
      // history.state is passed straight through: Turbo Drive keeps its restoration
      // identifier in there, and nulling it out would break Turbo's scroll/state
      // restoration on back/forward. replaceState (not pushState) so pause/resume
      // does not litter the back button with dead history entries.
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
    } catch (_e) {
      /* history/URL unavailable — the feed just won't stay frozen across a refresh */
    }
  }

  // A mark stamped BEFORE the event-tz day the server just rendered. See the
  // dayStart value and _restorePauseState.
  _isStaleDay(mark) {
    return !!(mark && this.dayStartValue && mark < this.dayStartValue)
  }

  // LRPO-5363 (code review) — ask the parent dashboard to reconcile the feed from
  // the server NOW. Used when this page was rendered FROZEN for a client that
  // believes it is live: the stale list is already in the DOM, and the interval
  // poller that would eventually replace it only runs when the cable is DOWN
  // (live_dashboard_controller#_startPolling is called from the disconnect/reject/
  // never-connected paths only), so on a healthy connection nothing would repair it.
  //
  // Deferred to the next macrotask so the parent is guaranteed to have bound its
  // data-action listener regardless of the order Stimulus connects the two.
  _requestLiveRefresh() {
    this._staleFeedSignal = setTimeout(() => {
      this._staleFeedSignal = null
      this.dispatch("staleFeed") // → "live-feed:staleFeed" (bubbles to the dashboard root)
    }, 0)
  }

  _renderPauseButton(paused) {
    if (!this.hasPauseButtonTarget) return
    const btn   = this.pauseButtonTarget
    const label = paused ? "Resume scan feed" : "Pause scan feed"
    const text  = paused ? "Resume" : "Pause"
    const icon  = paused ? "bi-play-fill" : "bi-pause-fill"

    btn.setAttribute("aria-label", label)
    btn.setAttribute("aria-pressed", String(paused))

    // Rebuild via createElement + textContent (XSS-hardened, matches
    // _buildScanNode style — never innerHTML).
    while (btn.firstChild) btn.removeChild(btn.firstChild)
    const i = document.createElement("i")
    i.className = `bi ${icon}`
    i.setAttribute("aria-hidden", "true")
    btn.appendChild(i)
    btn.appendChild(document.createTextNode(` ${text}`))
  }

  // LRPO-5302 — coalesce a live BURST into ONE frame. The server broadcasts one
  // cable frame PER LeadScan, so a mobile OFFLINE SYNC of N leads delivers N
  // prependScan calls in the SAME tick. This used to arm a rAF per scan and
  // defer each insert by `idx * 50ms`, so the synced scans trickled into the
  // feed one by one over N*50ms — the "scans appear one by one after sync"
  // report. Now the first scan of a burst arms exactly ONE frame; every scan
  // queued before it runs is drained by that same frame (mirroring the batch
  // _flushBuffer fix), so the whole sync lands in a single DOM update.
  _enqueueForAnimation(scan) {
    this._animQueue.push(scan)
    if (this._animFlushArmed) return // a flush is already armed for this burst

    // Flag set BEFORE the rAF call so re-entrant pushes within this tick see it,
    // regardless of what handle (or none) the host's rAF returns.
    this._animFlushArmed = true
    this._animFrame = window.requestAnimationFrame(() => {
      this._animFlushArmed = false
      this._animFrame = null
      this._drainAnimQueue()
    })
  }

  // Drains the whole queue synchronously inside one frame → one reflow/paint.
  _drainAnimQueue() {
    const queued = this._animQueue.splice(0)
    if (!queued.length) return

    // LRPO-5135 — MID-FLIGHT pause re-check. Scans staged here BEFORE a pause
    // whose flush runs AFTER the pause must NOT land in the live list. Re-route
    // them into the paused buffer (and bump the banner) so they surface on
    // resume in order, like any other buffered scan. pausedValue cannot change
    // mid-drain (the loop is synchronous), so one check covers the batch.
    //
    // LRPO-5367 — batched: this drains a BURST (a 50-lead mobile offline sync is
    // one), and the per-scan push path re-serialized the whole buffer to
    // sessionStorage once per scan. Enqueue the batch, then render + persist once.
    if (this.pausedValue) {
      queued.forEach(scan => this._enqueueBuffered(scan))
      this._renderBufferedCount(this._bufferCount)
      this._writePersistedBuffer()
      return
    }

    if (!this.hasListTarget) return
    // _insertOrReplace still dedups same-lead rescans and prepends newest-on-top;
    // _trim runs once after the batch rather than per entry.
    queued.forEach(scan => this._insertOrReplace(scan))
    this._trim()
  }

  _cancelAnimFlush() {
    if (this._animFrame != null) window.cancelAnimationFrame(this._animFrame)
    this._animFrame = null
    this._animFlushArmed = false
  }

  _flushBuffer() {
    // LRPO-5135 — drain the PAUSED buffer (not the animation queue) in original
    // arrival order into the live list, then clear the banner/count.
    // LRPO-5302 — flush as ONE batch: insert synchronously (no per-entry
    // requestAnimationFrame/setTimeout stagger), so all scans that accumulated
    // during the pause land in a single DOM update on resume instead of
    // trickling in one by one (slow + hard to read when many are buffered). The
    // synchronous loop mutates the DOM within one frame → a single reflow/paint.
    // _insertOrReplace still dedups same-lead rescans and prepends newest-on-top;
    // _trim runs once after the batch.
    const queued = this._buffer.splice(0)
    if (this.hasListTarget) {
      queued.forEach(scan => this._insertOrReplace(scan))
      this._trim()
    }
    this._bufferCount = 0
    this._renderBufferedCount(0)
    // LRPO-5367 (code-review re-open) — _seenLeadIds is deliberately NOT cleared
    // here. The flushed scans have just been RENDERED; forgetting them would make
    // the very next paused poll (showBuffered keeps the feed paused — LRPO-5368)
    // rediscover them as "new" and re-raise the banner for rows already on screen.
    // The old _polledLeadIds was cleared here because it only meant "already
    // counted"; a seen-set means "the user knows about it", which a flush confirms.
    if (this.pausedValue) {
      this._writePersistedBuffer() // still paused: keep the (now empty) record + its seen-set
    } else {
      this._clearPersistedBuffer() // LRPO-5363 — resumed and flushed, so drop the persisted copy
    }
  }

  // LRPO-5242 — dedup by stable per-lead key. The server feed is per-Lead
  // (one <li id="live-scan-<lead_id>"> per lead), so a RESCAN of the same lead
  // must REPLACE its existing row rather than append a duplicate. We remove the
  // stale node (if any) and prepend a freshly-built one carrying the latest
  // payload — which floats the row to the top with the new timestamp. Scans
  // from paths that don't supply lead_id (legacy/import broadcasts) fall back to
  // plain prepend, preserving prior behavior.
  _insertOrReplace(scan) {
    if (!this.hasListTarget) return
    const existing = this._existingNodeFor(scan)
    if (existing) existing.remove()
    this.listTarget.insertBefore(this._buildScanNode(scan), this.listTarget.firstChild)
    // LRPO-5363 (code review, round 4) — it is ON SCREEN now, so the pause mark may
    // never again be stamped behind it. This is the single choke point for "a scan
    // entered the LIVE list": the animation drain (a cable scan) and the buffer flush
    // (showBuffered / resume) both come through here.
    this._noteSeenScan(scan)
    // LRPO-5367 — the user is now looking at this lead, so a later paused poll must
    // not offer it back as a "new scan". Belt and braces with the row itself, which
    // _pruneSeenLeadIds also pins (re-open #3): this covers the lead BEFORE the next
    // prune runs, the pin keeps it covered once a snapshot that predates the scan
    // lands and would otherwise evict it.
    const key = this._leadKey(scan)
    if (key !== null) this._seenLeadIds.add(key)
    // LRPO-5363 (rework) — this row is on screen now; record its payload so a pause
    // can freeze it and a refresh can re-render it.
    if (key !== null) this._displayed.set(key, scan)
    this._syncEmptyState()
  }

  // LRPO-5301 — the "Waiting for your first scan…" placeholder is a SIBLING of
  // the list, rendered when the page loaded with zero scans. Hide it once any
  // scan lands (and re-show if the list is emptied); previously it lingered
  // alongside the first real-time scan until a manual refresh.
  _syncEmptyState() {
    if (!this.hasEmptyStateTarget) return
    const hasItems = this.hasListTarget && this.listTarget.children.length > 0
    this.emptyStateTarget.classList.toggle("d-none", hasItems)
  }

  _existingNodeFor(scan) {
    if (scan == null || scan.lead_id == null) return null
    // getElementById is document-wide; scope the lookup to THIS feed's list so
    // two dashboards on one page never cross-replace.
    return this.listTarget.querySelector(`#live-scan-${scan.lead_id}`)
  }

  _trim() {
    if (!this.hasListTarget) return
    const items = this.listTarget.querySelectorAll("[data-live-scan-item]")
    for (let i = this.maxItemsValue; i < items.length; i++) items[i].remove()
    // LRPO-5363 (rework) — keep the displayed-payload map bounded to the rows that
    // are STILL rendered, so a long session can't grow it past the trimmed list.
    if (this._displayed && this._displayed.size) {
      const live = this._renderedLeadIds()
      this._displayed.forEach((_v, key) => { if (!live.has(key)) this._displayed.delete(key) })
    }
  }

  _renderBufferedCount(n) {
    // LRPO-5367 — the banner surfaces BOTH buffered new scans AND pending
    // lead-data edits, so it shows whenever either is present.
    const pending = n > 0 || this._pendingEdits
    const text    = pending ? this._bufferedBannerText(n, this._pendingEdits) : ""

    if (this.hasBufferedTarget) {
      if (pending) {
        this.bufferedTarget.classList.remove("d-none")
        this.bufferedTarget.textContent = text
        // LRPO-5367 (QA re-open) — a button's aria-label OVERRIDES its visible
        // text, so the banner's static label ("Show new scans (feed stays
        // paused)") was ALL a screen reader ever got when it reached the button:
        // the pending COUNT — the indicator itself — never made it. Keep the label
        // in lock-step with the visible text so both audiences learn the same
        // thing.
        this.bufferedTarget.setAttribute("aria-label", `${text} (feed stays paused)`)
      } else {
        this.bufferedTarget.classList.add("d-none")
        // Clear the label with the state it described. Left behind, a hidden button
        // still advertises a stale count ("2 new scans…") that no longer exists —
        // and it would be the label the button reappears with on the next render,
        // before the branch above rewrites it.
        this.bufferedTarget.removeAttribute("aria-label")
      }
    }

    this._announce(text)
  }

  // LRPO-5367 (code-review re-open) — announce the pending state through a
  // SEPARATE, always-rendered live region rather than the banner button itself.
  // The button is `d-none` (display:none) until something is pending, and a live
  // region that is display:none at the moment its content changes is not tracked
  // by assistive tech — the announcement is simply lost, which is the entire
  // announcement, since the button only ever populates as it un-hides. The
  // announcer span is `visually-hidden` (clipped, NOT display:none), so it is in
  // the accessibility tree from first paint and every later mutation announces.
  //
  // Change-detection guard: the poll re-renders the banner on every cycle, and
  // re-writing an identical sentence makes NVDA/JAWS re-announce it — a paused
  // feed would nag every 30-60s. Only a genuine change speaks (same rationale as
  // live_dashboard_controller's _applyRepActivity guard).
  _announce(text) {
    if (!this.hasAnnouncerTarget) return
    if (text === this._lastAnnouncement) return
    this._lastAnnouncement = text
    this.announcerTarget.textContent = text
  }

  // LRPO-5367 — banner copy for the three paused states:
  //   scans only  → "N new scans — click to show"        (unchanged, 5368)
  //   scans+edits → "N new scans + updates — click to show"
  //   edits only  → "Lead updates while paused — click to refresh"
  _bufferedBannerText(n, edits) {
    if (n > 0) {
      const scans = `${n} new scan${n === 1 ? "" : "s"}`
      return edits ? `${scans} + updates — click to show` : `${scans} — click to show`
    }
    return "Lead updates while paused — click to refresh"
  }

  _buildScanNode(scan) {
    const li = document.createElement("li")
    li.className = "list-group-item py-3 live-dashboard-scan-feed__item live-dashboard-scan-feed__item--new"
    li.setAttribute("data-live-scan-item", "")
    // LRPO-5242 — stable per-lead key so a rescan REPLACES this row instead of
    // appending a duplicate. Matches the server-rendered _scan_entry
    // <li id="live-scan-<lead_id>">. Only set when the payload carries a
    // lead_id (live cable scans); legacy/import payloads without it are appended.
    if (scan && scan.lead_id != null) li.id = `live-scan-${scan.lead_id}`
    // LRPO-5363 (code review, round 4) — the row's SERVER-ISSUED scan instant, in the
    // same iso8601 the ERB writes (_scan_entry). It is what a LATER cold connect —
    // the refresh this whole ticket is about — reads back to learn how recent the list
    // it inherited is, so a JS-built row has to carry it exactly like a server-built
    // one, or a Turbo body swap would silently drop the floor to zero.
    if (scan && scan.timestamp) li.setAttribute("data-scan-at", scan.timestamp)

    const row = document.createElement("div")
    row.className = "d-flex justify-content-between align-items-start gap-2"

    const body = document.createElement("div")
    body.style.minWidth = "0"
    body.className = "flex-grow-1"

    const headline = document.createElement("div")
    headline.className = "text-truncate"
    const name = document.createElement("span")
    name.className = "fw-semibold"
    name.textContent = scan.attendee_name || "Attendee"
    headline.appendChild(name)

    if (scan.company) {
      const company = document.createElement("span")
      company.className = "text-muted small ms-2"
      company.textContent = scan.company
      headline.appendChild(company)
    }

    const meta = document.createElement("div")
    meta.className = "small text-muted mt-1"

    const byRep = document.createElement("span")
    byRep.textContent = `Scanned by ${scan.rep_name || "Scanner"}`
    meta.appendChild(byRep)

    // LRPO-5306 — per-lead daily scan-count badge. Shown for EVERY scanned lead
    // (scan_count >= 1, per the QA re-open — previously gated >= 2) with a class
    // DISTINCT from the qualifier chips (`live-scan-feed__count`, a brand-primary
    // tint) so the two are easy to tell apart. SAME threshold + class as the ERB
    // _scan_entry so initial render, broadcast, and poll never disagree. Both the
    // cable and poll payloads flow through here. Number(undefined|null) → NaN,
    // not >= 1, so an absent count renders no badge.
    if (Number(scan.scan_count) >= 1) {
      const countBadge = document.createElement("span")
      countBadge.className = "badge live-scan-feed__count ms-1"
      countBadge.textContent = `Scans: ${scan.scan_count}`
      meta.appendChild(countBadge)
    }

    // LRPO-5361 — render ALL selected qualifiers (was capped at slice(0, 3)).
    const qualifiers = Array.isArray(scan.qualifiers) ? scan.qualifiers : []
    if (qualifiers.length) {
      meta.appendChild(document.createTextNode(" · "))
      qualifiers.forEach(q => {
        const badge = document.createElement("span")
        badge.className = "badge bg-light text-dark border me-1"
        badge.textContent = q
        meta.appendChild(badge)
      })
    }

    body.appendChild(headline)
    body.appendChild(meta)

    const ts = document.createElement("small")
    ts.className = "text-muted text-nowrap"
    ts.textContent = scan.timestamp
      ? new Date(scan.timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: this.tzValue })
      : ""

    row.appendChild(body)
    row.appendChild(ts)
    li.appendChild(row)
    return li
  }
}
